import { useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, Monitor, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import CameraCaptureModal from '../camera/CameraCaptureModal';
import { canLinkFrom, linkCaptureToDestination } from '../camera/cameraActions';
import { fmtSec } from '../../utils/cameraCapture';
import { useWorkflowStore } from '../../store/workflowStore';
import { callSourcePickerHandler, clearSourcePickerHandler } from '../../utils/sourcePickerBridge';

export default function SourcePickerModal() {
  const sourcePicker = useWorkflowStore(s => s.sourcePicker);
  const closeSourcePicker = useWorkflowStore(s => s.closeSourcePicker);
  const setSourcePickerStep = useWorkflowStore(s => s.setSourcePickerStep);
  const fileInputRef = useRef(null);

  const handleClose = useCallback(() => {
    clearSourcePickerHandler();
    closeSourcePicker();
  }, [closeSourcePicker]);

  const handleGallery = useCallback(() => {
    setSourcePickerStep('gallery');
  }, [setSourcePickerStep]);

  const handleComputer = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      callSourcePickerHandler({ file });
      closeSourcePicker();
    }
    e.target.value = '';
  }, [closeSourcePicker]);

  // AP-11 · la cámara sirve cuando el destino acepta imagen o video
  const types = sourcePicker?.acceptTypes || [];
  const camAccept = types.includes('image') && types.includes('video') ? 'both'
    : types.includes('video') ? 'video' : types.includes('image') ? 'image' : null;

  const handleCamera = useCallback(() => setSourcePickerStep('camera'), [setSourcePickerStep]);

  const finishCapture = useCallback((result, kind, destKey) => {
    const nodeId = sourcePicker?.nodeId;
    callSourcePickerHandler(result);
    closeSourcePicker();
    if (destKey && destKey !== 'none') {
      const link = linkCaptureToDestination({ sourceNodeId: nodeId, kind, destKey });
      if (link?.connected) {
        toast.success(`Listo: conectado a ${link.label}.`, { position: 'bottom-right' });
        return;
      }
    }
    if (kind === 'video' && result?.duration) {
      toast.success(`Video listo (${fmtSec(result.duration)}).`, { position: 'bottom-right' });
    }
  }, [sourcePicker, closeSourcePicker]);

  if (sourcePicker?.step === 'camera' && camAccept) {
    const node = useWorkflowStore.getState().nodes?.find((n) => n.id === sourcePicker.nodeId);
    const linkKind = canLinkFrom(sourcePicker.nodeId);
    return (
      <CameraCaptureModal
        accept={camAccept}
        targetLabel={node ? `${node.data?.label || nodeLabel(node.type)}` : ''}
        linkKind={linkKind}
        onPhoto={(file, dest) => finishCapture({ file }, 'photo', dest)}
        onVideo={(out, dest) => finishCapture({
          url: out.url, fileName: out.original_name, fromCamera: true, duration: out.duration,
        }, 'video', dest)}
        onUpload={() => { setSourcePickerStep('choose'); setTimeout(() => fileInputRef.current?.click(), 0); }}
        onClose={handleClose}
      />
    );
  }

  if (!sourcePicker || sourcePicker.step !== 'choose') return null;

  return createPortal(
    <div className="source-picker-overlay" onClick={handleClose} data-testid="source-picker-modal">
      <div className="source-picker-modal" onClick={e => e.stopPropagation()}>
        <button className="source-picker-close" onClick={handleClose}>
          <X size={18} />
        </button>
        <h3 className="source-picker-title">Cargar archivo</h3>
        <p className="source-picker-subtitle">Elige de donde obtener tu archivo</p>
        <div className="source-picker-options">
          <button className="source-picker-option gallery" onClick={handleGallery} data-testid="source-from-gallery">
            <FolderOpen size={28} />
            <span>Desde la galeria</span>
            <small>Usa archivos que ya generaste</small>
          </button>
          <button className="source-picker-option computer" onClick={handleComputer} data-testid="source-from-computer">
            <Monitor size={28} />
            <span>Desde el ordenador</span>
            <small>Sube un archivo nuevo</small>
          </button>
          {camAccept && (
            <button className="source-picker-option camera" onClick={handleCamera} data-testid="source-from-camera">
              <Camera size={28} />
              <span>Con la cámara</span>
              <small>{camAccept === 'video' ? 'Grabá un clip corto' : camAccept === 'image' ? 'Sacá una foto' : 'Foto o video'}</small>
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={sourcePicker.acceptInput || '*/*'}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>,
    document.body
  );
}

const NODE_LABELS = {
  uploadImage: 'Cargar Imagen', uploadVideo: 'Cargar Video', dreamActor: 'Dream Actor',
  lipSync: 'LipSync', videoWithSound: 'Video con sonido', banana2pro: 'Banana 2 Pro',
};
function nodeLabel(type) { return NODE_LABELS[type] || type; }
